import { useState, useEffect } from "react";
import { evidenceApi } from "../../services/evidenceApi.js";
import { recordString } from "../../core/complaints/cmtsComplaintLifecycleTypes.js";
import type { CmtsEvidenceRecord } from "../../core/complaints/cmtsComplaintLifecycleTypes.js";

export function CmtsEvidencePreviewPanel({
  complaintId,
  row,
  onClose,
}: {
  readonly complaintId: string;
  readonly row: CmtsEvidenceRecord | null;
  readonly onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const evidenceId = row ? recordString(row, ["id", "evidenceId"], "") : "";
  const fileName = row ? recordString(row, ["originalFileName", "fileName", "name", "title"], "evidence") : "";
  const mimeType = row ? recordString(row, ["mimeType", "fileType"], "") : "";
  const isImage = mimeType.startsWith("image/");
  const isPdf = mimeType === "application/pdf";
  const isPreviewable = isImage || isPdf;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    if (!row || !isPreviewable) {
      setBlobUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const tokenResponse = await evidenceApi.downloadToken(complaintId, evidenceId);
        const token = recordString(tokenResponse, ["token", "downloadToken"], "");
        if (!token) throw new Error("download_token_failed: backend did not return an evidence download token.");
        const blob = await evidenceApi.downloadBlob(token);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Preview failed to load.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceId, isPreviewable, row]);

  useEffect(() => {
    if (!row) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [row, onClose]);

  if (!row) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">Evidence preview</p>
            <p className="text-xs text-slate-400 truncate">{fileName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close preview"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!isPreviewable ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <span className="text-4xl" aria-hidden>📄</span>
              <p className="text-sm text-slate-600">{fileName}</p>
              <p className="text-xs text-slate-400">No inline preview for this file type</p>
            </div>
          ) : null}

          {isPreviewable && loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-500">Loading preview…</div>
          ) : null}

          {isPreviewable && error ? (
            <div className="flex items-center justify-center py-16 text-sm text-red-600">{error}</div>
          ) : null}

          {isPreviewable && !loading && !error && blobUrl && isImage ? (
            <img src={blobUrl} alt={fileName} className="mx-auto max-h-[65vh] max-w-full object-contain rounded-xl" />
          ) : null}

          {isPreviewable && !loading && !error && blobUrl && isPdf ? (
            <iframe src={blobUrl} title={fileName} className="h-[65vh] w-full rounded-xl border" />
          ) : null}
        </div>
      </div>
    </div>
  );
}